import { RefreshCw, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { useOnline } from '../lib/online';
import { Button } from './ui/button';

/** Look for a new version this often while the app stays open. */
const UPDATE_CHECK_MS = 60 * 60 * 1000;

/**
 * Registers the service worker and shows the two states that matter on a
 * phone: no connection (scans are kept and sent later) and a new version
 * waiting. The update is never forced, since the app is often open mid-scan.
 */
export function PwaStatus() {
  const { t } = useTranslation();
  const online = useOnline();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      setInterval(() => void registration.update().catch(() => undefined), UPDATE_CHECK_MS);
    },
  });

  if (online && !needRefresh) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-40 flex flex-col items-center gap-2 px-4 md:bottom-4">
      {!online ? (
        <p
          role="status"
          className="flex items-center gap-2 rounded-full border bg-surface px-3 py-1.5 text-xs text-foreground shadow-lg"
        >
          <WifiOff className="size-3.5 text-muted" />
          {t('pwa.offline')}
        </p>
      ) : null}
      {needRefresh ? (
        <div
          role="status"
          className="pointer-events-auto flex items-center gap-3 rounded-panel border bg-surface px-4 py-2 text-sm text-foreground shadow-lg"
        >
          <span>{t('pwa.updateReady')}</span>
          <Button size="sm" onClick={() => void updateServiceWorker(true)}>
            <RefreshCw className="size-4" />
            {t('pwa.reload')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setNeedRefresh(false)}>
            {t('pwa.later')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
